const FUNCTIONS_BASE = "https://us-central1-kuramatura-7a9ba.cloudfunctions.net/api";

export interface ExplainPayload {
  question: string;       // question HTML with img replaced by descriptions
  partHtml: string;       // part.htmlContent with img replaced, or empty string
  questionType: string | undefined;
  correctAnswer: unknown;
  userAnswer: unknown;
  testType: string | undefined;
}

export interface ExplainResponse {
  explanation: string;
  conversationId: string;
  promptVersion: string;
}

export interface MessageEntry {
  role: "user" | "assistant";
  content: string;
}

/**
 * Replace <img src="..."> tags with [Image: description] using the asset context map,
 * then strip remaining HTML tags, leaving plain readable text.
 */
export function resolveHtml(html: string, assetContext: Record<string, string>): string {
  return html
    .replace(/<img[^>]*src="([^"]*)"[^>]*\/?>/gi, (_, src) => {
      const filename = src.split('/').pop() ?? src
      const desc = assetContext[filename]
      return desc ? `[Image: ${desc}]` : ''
    })
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export async function fetchExplanation(payload: ExplainPayload): Promise<ExplainResponse> {
  const res = await fetch(`${FUNCTIONS_BASE}/explain`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`explain: ${res.status}`);
  return res.json();
}

export async function fetchFollowUp(
  history: MessageEntry[],
  followUp: string,
  payload: Pick<ExplainPayload, 'testType' | 'questionType' | 'partHtml'>
): Promise<string> {
  const res = await fetch(`${FUNCTIONS_BASE}/explain/followup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ history, followUp, ...payload }),
  });
  if (!res.ok) throw new Error(`followup: ${res.status}`);
  const data = await res.json();
  return data.reply as string;
}

export async function submitRating(
  conversationId: string,
  rating: "up" | "down",
  promptVersion: string,
  payload: ExplainPayload
): Promise<void> {
  await fetch(`${FUNCTIONS_BASE}/explain/rate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      conversationId,
      rating,
      promptVersion,
      testType: payload.testType,
      questionType: payload.questionType,
      question: payload.question,
      correctAnswer: payload.correctAnswer,
      userAnswer: payload.userAnswer,
    }),
  });
}
